import { useState } from "react";
import { Link } from "react-router-dom";
import { Droplets, Plus } from "lucide-react";
import { toast } from "sonner";
import { Empty, Field, Kpi, NSelect, Plate, useFetch } from "@/components/common";
import { ConfirmDialog } from "@/components/DemoCleanup";
import { PhotoSlot } from "@/components/Photos";
import { WarningsBox, warningsOf } from "@/components/Pleins";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, fDate, fEur, fNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const pad = (x) => String(x).padStart(2, "0");
const now = () => {
  const d = new Date();
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};
const num = (s) => (s === "" || s == null ? null : Number(String(s).replace(",", ".")));

export function AdBlueDialog({ vehicleId, onClose, onDone }) {
  const list = useFetch(vehicleId ? null : "/adblue/summary");
  const [f, setF] = useState({ vehicle_id: vehicleId || "", ...now(), mileage: "", liters: "", amount_ttc: "", station: "", comment: "" });
  const [photos, setPhotos] = useState([]);
  const [warn, setWarn] = useState(null);
  const set = (k) => (e) => { setF({ ...f, [k]: e?.target ? e.target.value : e }); setWarn(null); };
  const run = async () => {
    try {
      await api.post("/adblue", { ...f, mileage: Math.round(num(f.mileage) || 0), liters: num(f.liters) || 0, amount_ttc: num(f.amount_ttc), photos, confirm: !!warn });
      toast.success("Ajout d'AdBlue enregistré");
      onDone();
    } catch (e) {
      const w = warningsOf(e);
      if (w) setWarn(w);
      else toast.error(errMsg(e));
    }
  };
  return (
    <ConfirmDialog title="Ajouter de l'AdBlue" onConfirm={run} onClose={onClose} label={warn ? "Confirmer quand même" : "Enregistrer"} testid="adblue-dialog" primary>
      {!vehicleId && <Field label="Véhicule *"><NSelect value={f.vehicle_id} onChange={set("vehicle_id")} placeholder="Choisir…" testid="adblue-vehicle" options={(list.data?.vehicles || []).map((v) => ({ value: v.id, label: `${v.plate} · ${v.brand || ""}` }))} /></Field>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date *"><Input type="date" value={f.date} onChange={set("date")} data-testid="adblue-date" /></Field>
        <Field label="Heure"><Input type="time" value={f.time} onChange={set("time")} data-testid="adblue-time" /></Field>
        <Field label="Kilométrage compteur *"><Input inputMode="numeric" value={f.mileage} onChange={set("mileage")} data-testid="adblue-mileage" /></Field>
        <Field label="Litres d'AdBlue *"><Input inputMode="decimal" value={f.liters} onChange={set("liters")} data-testid="adblue-liters" /></Field>
        <Field label="Montant TTC (€)"><Input inputMode="decimal" value={f.amount_ttc} onChange={set("amount_ttc")} data-testid="adblue-ttc" /></Field>
        <Field label="Fournisseur / station"><Input value={f.station} onChange={set("station")} data-testid="adblue-station" /></Field>
      </div>
      <Field label="Commentaire"><Input value={f.comment} onChange={set("comment")} data-testid="adblue-comment" /></Field>
      <div className="w-40"><PhotoSlot label="Justificatif (facultatif)" ids={photos} onChange={setPhotos} single testid="adblue-photo" /></div>
      <WarningsBox warnings={warn} testid="adblue-warnings" />
    </ConfirmDialog>
  );
}

export function AdBlueToggle({ vehicle, onDone }) {
  const { user } = useAuth();
  if (!user.canEdit) return null;
  const toggle = async (enabled) => {
    try {
      await api.patch(`/vehicles/${vehicle.id}/adblue`, { enabled });
      toast.success(enabled ? "AdBlue activé pour ce véhicule" : "AdBlue désactivé pour ce véhicule");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-600" data-testid="adblue-toggle">
      <Switch checked={!!vehicle.uses_adblue} onCheckedChange={toggle} data-testid="adblue-toggle-switch" /> Utilise de l'AdBlue
    </label>
  );
}

export function AdBlueHistory({ vehicleId, k = 0 }) {
  const { data } = useFetch("/adblue", { vehicle_id: vehicleId, k });
  if (!data) return null;
  const s = data.stats;
  return (
    <div data-testid="adblue-history">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-3">
        <Kpi testid="adblue-total" label="Total ajouté" value={`${fNum(s.total_liters, 1)} L`} icon={Droplets} tone="navy" />
        <Kpi testid="adblue-count" label="Nombre d'ajouts" value={s.count} icon={Plus} />
        <Kpi testid="adblue-km" label="Km suivis" value={s.km_tracked ? `${fNum(s.km_tracked)} km` : "—"} icon={Droplets} />
        <Kpi testid="adblue-per1000" label="Consommation estimée" value={s.per_1000km != null ? `${fNum(s.per_1000km, 2)} L / 1 000 km` : "—"} sub={s.per_1000km == null ? "Données kilométriques insuffisantes" : null} icon={Droplets} />
      </div>
      {!data.entries.length ? <Empty text="Aucun ajout d'AdBlue enregistré" /> : (
        <div className="overflow-x-auto"><table className="tbl w-full">
          <thead><tr><th>Date</th><th className="text-right">Kilométrage</th><th className="text-right">Litres</th><th className="text-right">Distance depuis l'ajout précédent</th><th className="text-right">TTC</th><th>Saisi par</th><th>Commentaire</th></tr></thead>
          <tbody>{data.entries.map((e) => (
            <tr key={e.id} data-testid={`adblue-row-${e.id}`}>
              <td className="whitespace-nowrap">{fDate(e.date)} <span className="text-xs text-slate-400">{e.time || ""}</span></td>
              <td className="text-right tabular-nums">{fNum(e.mileage)} km{e.anomaly_confirmed && <span className="ml-1 text-[10px] font-bold text-amber-700" title={(e.warnings || []).join(" · ")}>anomalie confirmée</span>}</td>
              <td className="text-right tabular-nums font-semibold">{fNum(e.liters, 1)} L</td>
              <td className="text-right tabular-nums">{e.distance != null ? `${fNum(e.distance)} km` : "—"}</td>
              <td className="text-right tabular-nums">{e.amount_ttc != null ? fEur(e.amount_ttc) : "—"}</td>
              <td>{e.user_name}</td>
              <td className="whitespace-normal text-xs">{e.comment || "—"}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}

export function AdBluePanel() {
  const { data } = useFetch("/adblue/summary");
  if (!data?.vehicles.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6" data-testid="adblue-dashboard-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold text-[#0E2D43] flex items-center gap-2"><Droplets className="w-4 h-4 text-[#2F80ED]" /> AdBlue</div>
        <Link to="/livraison/adblue" className="text-sm font-semibold text-[#E0830B] hover:underline" data-testid="adblue-dashboard-link">Historique complet</Link>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">{data.vehicles.map((v) => (
        <Link key={v.id} to={`/livraison/vehicules/${v.id}?tab=adblue`} className="rounded-lg border border-slate-200 p-3 hover:border-[#2F80ED] transition-colors" data-testid={`adblue-dash-${v.plate}`}>
          <div className="flex items-center gap-2"><Plate>{v.plate}</Plate><span className="text-xs text-slate-500">{v.brand} {v.model}</span></div>
          <div className="text-sm mt-1">{v.last ? `Dernier ajout : ${fNum(v.last.liters, 1)} L le ${fDate(v.last.date)} à ${fNum(v.last.mileage)} km` : "Aucun ajout enregistré"}</div>
          <div className="text-xs text-slate-500">{v.count} ajout(s) · {fNum(v.total_liters, 1)} L au total</div>
        </Link>
      ))}</div>
    </div>
  );
}
